import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText,
  Calendar,
  DollarSign,
  Trash2,
  Edit2,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  PenLine,
  Send
} from 'lucide-react';

const STATUS_CONFIG = {
  planning: { label: 'Planning', icon: PenLine, color: 'bg-stone-100 text-stone-700' },
  drafting: { label: 'Drafting', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-700' },
  submitted: { label: 'Submitted', icon: Send, color: 'bg-blue-100 text-blue-700' },
  awarded: { label: 'Awarded', icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-700' },
};

export default function Grants() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [grants, setGrants] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    funder_name: '',
    funder_website: '',
    deadline: '',
    amount_requested: '',
    status: 'planning',
    program_id: '',
    requirements: '',
    notes: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchGrants();
    fetchPrograms();
    fetchStats();
  }, [user, navigate]);

  const fetchGrants = async (status = null) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const url = status && status !== 'all'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/grants?status=${status}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/grants`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setGrants(data);
      }
    } catch (error) {
      console.error('Error fetching grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grants/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      funder_name: '',
      funder_website: '',
      deadline: '',
      amount_requested: '',
      status: 'planning',
      program_id: '',
      requirements: '',
      notes: '',
    });
  };

  const handleAddGrant = async () => {
    if (!formData.name || !formData.funder_name) {
      toast.error('Grant name and funder name are required');
      return;
    }
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          amount_requested: formData.amount_requested ? parseFloat(formData.amount_requested) : null,
        }),
      });

      if (response.ok) {
        toast.success('Grant added successfully');
        setIsAddDialogOpen(false);
        resetForm();
        fetchGrants();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add grant');
      }
    } catch (error) {
      console.error('Error adding grant:', error);
      toast.error('Failed to add grant');
    }
  };

  const handleUpdateGrant = async () => {
    if (!selectedGrant) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grants/${selectedGrant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          amount_requested: formData.amount_requested ? parseFloat(formData.amount_requested) : null,
        }),
      });

      if (response.ok) {
        toast.success('Grant updated');
        setIsEditDialogOpen(false);
        setSelectedGrant(null);
        resetForm();
        fetchGrants();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating grant:', error);
      toast.error('Failed to update grant');
    }
  };

  const handleStatusChange = async (grantId, newStatus) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grants/${grantId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchGrants(activeTab);
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteGrant = async (grantId) => {
    if (!confirm('Are you sure you want to delete this grant?')) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grants/${grantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Grant deleted');
        fetchGrants();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting grant:', error);
    }
  };

  const openEditDialog = (grant) => {
    setSelectedGrant(grant);
    setFormData({
      name: grant.name || '',
      funder_name: grant.funder_name || '',
      funder_website: grant.funder_website || '',
      deadline: grant.deadline || '',
      amount_requested: grant.amount_requested ? String(grant.amount_requested) : '',
      status: grant.status || 'planning',
      program_id: grant.program_id || '',
      requirements: grant.requirements || '',
      notes: grant.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    fetchGrants(value);
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.name : '';
  };

  const isDeadlineSoon = (deadline) => {
    if (!deadline) return false;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const daysUntil = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    return daysUntil <= 14 && daysUntil >= 0;
  };

  const GrantForm = ({ onSubmit, buttonText }) => (
    <div className="space-y-4 mt-4">
      <div>
        <Label>Grant Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Community Development Grant 2025"
          data-testid="grant-name-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Funder Name *</Label>
          <Input
            value={formData.funder_name}
            onChange={(e) => setFormData({ ...formData, funder_name: e.target.value })}
            placeholder="Community Trust Foundation"
          />
        </div>
        <div>
          <Label>Funder Website</Label>
          <Input
            value={formData.funder_website}
            onChange={(e) => setFormData({ ...formData, funder_website: e.target.value })}
            placeholder="https://foundation.org"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Deadline</Label>
          <Input
            type="date"
            value={formData.deadline}
            onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
          />
        </div>
        <div>
          <Label>Amount Requested ($)</Label>
          <Input
            type="number"
            value={formData.amount_requested}
            onChange={(e) => setFormData({ ...formData, amount_requested: e.target.value })}
            placeholder="50000"
          />
        </div>
        <div>
          <Label>Status</Label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full h-10 px-3 border border-stone-200 rounded-md"
          >
            <option value="planning">Planning</option>
            <option value="drafting">Drafting</option>
            <option value="submitted">Submitted</option>
            <option value="awarded">Awarded</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      <div>
        <Label>Program (for grant application)</Label>
        <select
          value={formData.program_id}
          onChange={(e) => setFormData({ ...formData, program_id: e.target.value })}
          className="w-full h-10 px-3 border border-stone-200 rounded-md"
        >
          <option value="">Select a program...</option>
          {programs.map(program => (
            <option key={program.id} value={program.id}>{program.name}</option>
          ))}
        </select>
      </div>
      <div>
        <Label>Requirements</Label>
        <Textarea
          value={formData.requirements}
          onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
          placeholder="List the grant requirements..."
          rows={2}
        />
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
        />
      </div>
      <Button onClick={onSubmit} className="w-full bg-primary hover:bg-primary-800 text-white">
        {buttonText}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-alabaster" data-testid="grants-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary-900 mb-2">
              Grant Tracker
            </h1>
            <p className="text-stone-600">
              Track grant applications, deadlines, and outcomes
            </p>
          </div>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-800 text-white" data-testid="add-grant-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Grant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Grant</DialogTitle>
              </DialogHeader>
              <GrantForm onSubmit={handleAddGrant} buttonText="Add Grant" />
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-white border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
                    <p className="text-xs text-stone-500">Total Grants</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.by_status?.drafting || 0}</p>
                    <p className="text-xs text-stone-500">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.by_status?.submitted || 0}</p>
                    <p className="text-xs text-stone-500">Submitted</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">${(stats.total_awarded / 1000).toFixed(0)}k</p>
                    <p className="text-xs text-stone-500">Awarded</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.upcoming_deadlines}</p>
                    <p className="text-xs text-stone-500">Upcoming</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Grants List */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="bg-stone-100">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="planning">Planning</TabsTrigger>
                <TabsTrigger value="drafting">Drafting</TabsTrigger>
                <TabsTrigger value="submitted">Submitted</TabsTrigger>
                <TabsTrigger value="awarded">Awarded</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {grants.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700 mb-2">No grants yet</h3>
                <p className="text-stone-500 mb-6">
                  Start tracking your grant applications and deadlines
                </p>
                <Button 
                  onClick={() => setIsAddDialogOpen(true)}
                  className="bg-primary hover:bg-primary-800 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Grant
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {grants.map((grant) => {
                  const statusConfig = STATUS_CONFIG[grant.status] || STATUS_CONFIG.planning;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div 
                      key={grant.id} 
                      className="border border-stone-200 rounded-lg p-4 hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-stone-900">{grant.name}</h3>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            {isDeadlineSoon(grant.deadline) && (
                              <Badge className="bg-orange-100 text-orange-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Deadline Soon
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-stone-500 mb-2">
                            <span className="font-medium text-stone-700">{grant.funder_name}</span>
                            {grant.deadline && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Due: {new Date(grant.deadline).toLocaleDateString()}
                              </span>
                            )}
                            {grant.amount_requested && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <DollarSign className="w-3 h-3" />
                                ${grant.amount_requested.toLocaleString()}
                              </span>
                            )}
                            {grant.program_id && (
                              <span className="text-primary">
                                Program: {getProgramName(grant.program_id)}
                              </span>
                            )}
                          </div>

                          {grant.requirements && (
                            <p className="text-sm text-stone-600 line-clamp-1">{grant.requirements}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <select
                            value={grant.status}
                            onChange={(e) => handleStatusChange(grant.id, e.target.value)}
                            className="h-8 px-2 text-sm border border-stone-200 rounded-md"
                          >
                            <option value="planning">Planning</option>
                            <option value="drafting">Drafting</option>
                            <option value="submitted">Submitted</option>
                            <option value="awarded">Awarded</option>
                            <option value="rejected">Rejected</option>
                          </select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(grant)}
                            className="text-stone-500 hover:text-primary"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteGrant(grant.id)}
                            className="text-stone-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Grant</DialogTitle>
            </DialogHeader>
            <GrantForm onSubmit={handleUpdateGrant} buttonText="Save Changes" />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
